import { Icon } from "@blueprintjs/core";
import React, { ChangeEventHandler, KeyboardEventHandler, useCallback, useState } from "react";

import { PlayStateApi } from "../../hooks";
import { editIcon } from "../../images";
import { Sport } from "../../redux/slices";
import { Frame, SportPlay } from "../../types";
import { TextEditor } from "../text-editor/text-editor";
import styles from "./play-description.module.scss";

interface PlayDescriptionProps<TPlay> extends Pick<PlayStateApi, "updatePartialPlay" | "updateActiveFrame"> {
  frame: Frame;
  play: TPlay;
}

export const PlayDescription = <TPlay extends SportPlay>({
  frame,
  updateActiveFrame,
  play,
  updatePartialPlay,
}: PlayDescriptionProps<TPlay>) => {
  const { sport } = play;
  const [collasped, setCollasped] = useState<boolean>(sport === Sport.BASKETBALL ? false : true);

  /** ======== Save Frame ======== */
  const saveFrame = useCallback(() => {
    updatePartialPlay(play as Partial<TPlay>);
  }, [play, updatePartialPlay]);

  /** ======== Label ======== */
  const [isEditingLabel, setIsEditingLabel] = useState<boolean>(false);
  const handleClickEditLabel = () => {
    setIsEditingLabel(true);
  };
  // Update frame state when changing label
  const handleChangeFrameLabel: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateActiveFrame((prevState) => ({ ...prevState, label: event.target.value }));
  };
  // turn off isEditingLabel and call save frame
  const saveFrameLabel = useCallback(() => {
    setIsEditingLabel(false);
    saveFrame();
  }, [setIsEditingLabel, saveFrame]);

  // save label on enter
  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === "Enter") {
      saveFrameLabel();
    }
  };
  const handleOnBlur = () => {
    saveFrameLabel();
  };

  /** ======== Editor ======== */
  const onTextEditorBlur = useCallback(
    (text: string) => {
      const newPlay: Partial<TPlay> = {
        ...play,
        frames: play.frames.map((playFrame) => {
          if (playFrame.id === frame.id) {
            return { ...frame, description: text };
          } else return playFrame;
        }),
      };

      updatePartialPlay(newPlay);
    },
    [frame, play, updatePartialPlay]
  );

  const handleCollapseClick = () => {
    setCollasped(!collasped);
  };

  return (
    <div className={collasped ? styles.playDescriptionClosed : styles.playDescription}>
      {collasped ? (
        <Icon icon="double-chevron-left" onClick={handleCollapseClick} className={styles.collapse} />
      ) : (
        <Icon icon="double-chevron-right" onClick={handleCollapseClick} className={styles.collapse} />
      )}
      <div className={styles.playDescriptionHeader}>
        {!isEditingLabel && (
          <div className={styles.headerFrameLabel}>
            {frame.label}
            <img className={styles.drawImage} src={editIcon} onClick={handleClickEditLabel} />
          </div>
        )}
        {isEditingLabel && (
          <>
            <input
              value={frame.label}
              onChange={handleChangeFrameLabel}
              onKeyDown={handleKeyDown}
              onBlur={handleOnBlur}
              autoFocus
            />
          </>
        )}
      </div>
      <div className={collasped ? styles.playDescriptionBodyClosed : styles.playDescriptionBody}>
        <TextEditor htmlText={frame.description || ""} onTextEditorBlur={onTextEditorBlur} />
        {/* {enableCoachingPoints && play?.id && play?.coachingPoints?.[0]?.coachingPoints && (
          <CoachingPoints coachingPoints={play.coachingPoints[0].coachingPoints} playId={play?.id} />
        )} */}
      </div>
    </div>
  );
};
